/**
 * services/EconomicCalendarService.ts
 * Service for fetching and transforming economic calendar events
 *
 * Provides a provider-abstracted interface to fetch economic events from
 * external APIs (currently Finnhub via Edge Function). Handles transformation
 * to domain types, deduplication, and error classification.
 *
 * Architecture:
 * - Provider pattern for future API swap (Trading Economics, etc.)
 * - Result object pattern (no throws) for predictable error handling
 * - Singleton export per CODING_STANDARDS
 */

import { supabase } from '@/lib/supabase';
import { generateUUID } from '@/utils/uuid';
import type { EconomicEventDraft } from '@/types/transient/EconomicEventDraft';

// ============================================================================
// Types and Interfaces
// ============================================================================

/**
 * Finnhub Economic Calendar event shape (wire format from Edge Function)
 */
interface FinnhubEconomicEvent {
  country: string;
  event: string;
  impact: string | null;
  time: string;
  estimate: number | null;
  prev: number | null;
  actual: number | null;
  unit: string;
}

/**
 * Error codes matching Edge Function's ErrorCode union exactly
 */
type FetchErrorCode =
  | 'UNAUTHORIZED'
  | 'BAD_REQUEST'
  | 'PROVIDER_RATE_LIMITED'
  | 'PROVIDER_AUTH_ERROR'
  | 'TRANSIENT'
  | 'UNKNOWN_ERROR';

/**
 * Result type for service's fetchEvents method
 */
type FetchEventsResult =
  | { ok: true; events: EconomicEventDraft[] }
  | { ok: false; error: FetchErrorCode };

/**
 * Result type for provider's fetchEvents method
 */
type ProviderFetchResult =
  | { ok: true; events: FinnhubEconomicEvent[] }
  | { ok: false; error: FetchErrorCode };

/**
 * Provider interface for economic calendar data sources
 * Internal abstraction - not exported until multiple providers needed
 */
interface EconomicCalendarProvider {
  fetchEvents(from: string, to: string): Promise<ProviderFetchResult>;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Normalize a string for consistent ID generation
 * - Trims whitespace from ends
 * - Collapses internal whitespace to single space
 * - Lowercases the result
 */
function normalizeString(str: string): string {
  return str.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Generate a deterministic provider event ID from event components
 * Format: normalized(country)|normalized(event)|normalized(time)
 *
 * Note: Time is normalized with normalizeToISO8601UTC() so IDs stay consistent
 * regardless of Finnhub's datetime format (space vs T separator, with/without Z).
 */
function generateProviderEventId(
  country: string,
  event: string,
  time: string
): string {
  const normalizedTime = normalizeToISO8601UTC(time.trim());
  return `${normalizeString(country)}|${normalizeString(event)}|${normalizedTime}`;
}

/**
 * Map Finnhub impact string to schema ImpactLevel union
 * Returns undefined for null, empty, whitespace-only, or unexpected values
 */
function mapImpactLevel(
  impact: string | null
): 'low' | 'medium' | 'high' | undefined {
  if (impact === null) {
    return undefined;
  }

  const normalized = impact.trim().toLowerCase();

  if (normalized === '') {
    return undefined;
  }

  if (normalized === 'low' || normalized === 'medium' || normalized === 'high') {
    return normalized;
  }

  return undefined;
}

/**
 * Convert null to undefined for optional number fields
 */
function nullToUndefined(value: number | null): number | undefined {
  return value === null ? undefined : value;
}

/**
 * Check if a datetime string already has a timezone indicator
 * Matches: Z, z, +HH:MM, -HH:MM, +HHMM, -HHMM
 *
 * Note: Cannot use simple `includes('-')` because dates contain dashes (2026-01-20)
 */
function hasTimezoneIndicator(time: string): boolean {
  return /([zZ]|[+-]\d{2}:?\d{2})$/.test(time);
}

/**
 * Normalize datetime string to valid ISO-8601 UTC format
 *
 * Finnhub returns datetimes with space separator: "2026-01-14 13:30:00"
 * This is NOT valid ISO-8601 (requires 'T' separator) and causes unreliable
 * Date parsing across browsers.
 *
 * Transformation steps:
 * 1. Replace first space with 'T' if no 'T' exists (avoid double-munging)
 * 2. Append 'Z' timezone indicator if not present
 *
 * @example
 * "2026-01-14 13:30:00"   -> "2026-01-14T13:30:00Z"
 * "2026-01-14T13:30:00"   -> "2026-01-14T13:30:00Z"
 * "2026-01-14T13:30:00Z"  -> "2026-01-14T13:30:00Z" (unchanged)
 */
function normalizeToISO8601UTC(time: string): string {
  // Step 1: Ensure T separator (only replace first space if no T exists)
  let normalized = time;
  if (!time.includes('T')) {
    normalized = time.replace(' ', 'T');
  }

  // Step 2: Ensure UTC timezone indicator
  if (!hasTimezoneIndicator(normalized)) {
    normalized = normalized + 'Z';
  }

  return normalized;
}

// ============================================================================
// Skeleton Event Filtering
// ============================================================================
// Finnhub returns placeholder/skeleton events for dates where recurring events
// don't actually occur (e.g., daily entries for monthly releases like US Retail
// Sales). These skeleton events have impact level but no data values.
//
// Conservative filter: Only remove a skeleton event if there exists a matching
// "real" event (same country + name + time-of-day) within ±1 day that has actual
// data. This prevents hiding legitimate upcoming events that simply lack estimates.
// ============================================================================

/**
 * Check if an event is a skeleton/placeholder (no data values)
 * Skeleton events have: impactLevel present, but NO previous/forecast/actual/unit
 */
function isSkeletonEvent(event: EconomicEventDraft): boolean {
  const unitTrimmed = event.unit?.trim() ?? '';
  const hasNoValues =
    event.previous === undefined &&
    event.forecast === undefined &&
    event.actual === undefined &&
    unitTrimmed === '';

  return hasNoValues;
}

/**
 * Check if an event is a "real" event (has at least one data value)
 * Real events have ANY of: previous, forecast, actual, or non-empty unit
 */
function isRealEvent(event: EconomicEventDraft): boolean {
  const unitTrimmed = event.unit?.trim() ?? '';
  return (
    event.previous !== undefined ||
    event.forecast !== undefined ||
    event.actual !== undefined ||
    unitTrimmed !== ''
  );
}

/**
 * Extract time-of-day (HH:MM:SS) from ISO-8601 datetime string
 * Returns empty string if pattern not found
 *
 * @example
 * "2026-01-14T13:30:00Z" -> "13:30:00"
 */
function extractTimeOfDay(datetime: string): string {
  const match = datetime.match(/T(\d{2}:\d{2}:\d{2})/);
  return match?.[1] ?? '';
}

/**
 * Extract date portion (YYYY-MM-DD) from ISO-8601 datetime string
 */
function extractDatePart(datetime: string): string {
  return datetime.slice(0, 10);
}

/**
 * Check if two YYYY-MM-DD date strings are within ±1 day of each other
 * Uses Date.UTC to avoid locale/DST issues with date parsing
 */
function isWithinOneDay(date1: string, date2: string): boolean {
  // Parse YYYY-MM-DD directly to avoid locale issues
  const parts1 = date1.split('-');
  const parts2 = date2.split('-');

  const y1 = parseInt(parts1[0] ?? '0', 10);
  const m1 = parseInt(parts1[1] ?? '1', 10);
  const d1 = parseInt(parts1[2] ?? '1', 10);

  const y2 = parseInt(parts2[0] ?? '0', 10);
  const m2 = parseInt(parts2[1] ?? '1', 10);
  const d2 = parseInt(parts2[2] ?? '1', 10);

  const utc1 = Date.UTC(y1, m1 - 1, d1);
  const utc2 = Date.UTC(y2, m2 - 1, d2);

  const diffMs = Math.abs(utc1 - utc2);
  const oneDayMs = 24 * 60 * 60 * 1000;
  return diffMs <= oneDayMs;
}

/**
 * Check if a skeleton event has a matching "real" event within ±1 day
 *
 * Match criteria:
 * - Same country (normalized)
 * - Same name (normalized)
 * - Same UTC time-of-day
 * - Date is within ±1 day
 * - The matching event is a "real" event (has data values)
 */
function hasMatchingRealEvent(
  skeleton: EconomicEventDraft,
  allEvents: EconomicEventDraft[]
): boolean {
  // Cannot match if skeleton is missing required fields
  if (!skeleton.country || !skeleton.name || !skeleton.datetime) {
    return false;
  }

  const skeletonCountry = normalizeString(skeleton.country);
  const skeletonName = normalizeString(skeleton.name);
  const skeletonTimeOfDay = extractTimeOfDay(skeleton.datetime);
  const skeletonDate = extractDatePart(skeleton.datetime);

  for (const event of allEvents) {
    // Skip same event
    if (event.id === skeleton.id) {
      continue;
    }

    // Skip if not a real event
    if (!isRealEvent(event)) {
      continue;
    }

    // Skip if missing required fields for comparison
    if (!event.country || !event.name || !event.datetime) {
      continue;
    }

    // Check country match
    if (normalizeString(event.country) !== skeletonCountry) {
      continue;
    }

    // Check name match
    if (normalizeString(event.name) !== skeletonName) {
      continue;
    }

    // Check time-of-day match
    if (extractTimeOfDay(event.datetime) !== skeletonTimeOfDay) {
      continue;
    }

    // Check date is within ±1 day
    if (!isWithinOneDay(extractDatePart(event.datetime), skeletonDate)) {
      continue;
    }

    // Found a matching real event
    return true;
  }

  return false;
}

/**
 * Filter out skeleton events that have matching real events within ±1 day
 *
 * Only removes events that are BOTH:
 * 1. Skeleton events (no previous/forecast/actual/unit)
 * 2. Have a matching real event (same country + name + time-of-day) within ±1 day
 *
 * This conservative approach prevents hiding legitimate upcoming events.
 */
function filterSkeletonEvents(
  events: EconomicEventDraft[]
): EconomicEventDraft[] {
  return events.filter((event) => {
    // Keep all real events
    if (!isSkeletonEvent(event)) {
      return true;
    }

    // For skeleton events, only remove if matching real event exists
    const hasMatch = hasMatchingRealEvent(event, events);
    return !hasMatch;
  });
}

/**
 * Transform a Finnhub event to an EconomicEventDraft
 * Returns null if required fields (country, event, time) are missing
 *
 * Note: Uses spread pattern to conditionally include optional fields
 * to satisfy exactOptionalPropertyTypes (avoid assigning undefined)
 */
function transformToEventDraft(
  finnhubEvent: FinnhubEconomicEvent
): EconomicEventDraft | null {
  const { country, event, time, impact, estimate, prev, actual, unit } =
    finnhubEvent;

  // Validate required fields
  if (!country || !event || !time) {
    return null;
  }

  const now = new Date().toISOString();
  const impactLevel = mapImpactLevel(impact);
  const previous = nullToUndefined(prev);
  const forecast = nullToUndefined(estimate);
  const actualValue = nullToUndefined(actual);

  return {
    id: generateUUID(),
    createdAt: now,
    updatedAt: now,
    name: event,
    datetime: normalizeToISO8601UTC(time.trim()),
    country: country,
    source: 'api',
    providerEventId: generateProviderEventId(country, event, time),
    // Conditionally include optional fields only when defined
    ...(impactLevel !== undefined && { impactLevel }),
    ...(previous !== undefined && { previous }),
    ...(forecast !== undefined && { forecast }),
    ...(actualValue !== undefined && { actual: actualValue }),
    ...(unit && { unit }),
  };
}

/**
 * Check if an error code represents a transient (retry-able) error
 */
export function isTransientError(code: FetchErrorCode): boolean {
  return code === 'TRANSIENT' || code === 'PROVIDER_RATE_LIMITED';
}

// ============================================================================
// Provider Implementation
// ============================================================================

/**
 * Finnhub provider implementation
 * Wraps Edge Function call with error handling and response transformation
 */
class FinnhubProvider implements EconomicCalendarProvider {
  async fetchEvents(from: string, to: string): Promise<ProviderFetchResult> {
    try {
      const { data, error } = await supabase.functions.invoke(
        `finnhub-economic-calendar?from=${from}&to=${to}`,
        { method: 'GET' }
      );

      // Handle invoke error (network error OR non-2xx response)
      if (error) {
        return this.handleInvokeError(error);
      }

      // Handle unlikely case of error in 2xx response
      if (!data?.ok) {
        const errorCode = this.extractErrorCode(data);
        return { ok: false, error: errorCode };
      }

      // Success - return events array
      const events = (data.data as FinnhubEconomicEvent[]) ?? [];

      return { ok: true, events };
    } catch {
      // Unexpected error - treat as transient
      return { ok: false, error: 'TRANSIENT' };
    }
  }

  /**
   * Handle error from supabase.functions.invoke
   * Attempts to parse structured error from response body
   */
  private handleInvokeError(error: unknown): ProviderFetchResult {
    // Try to extract structured error from error.context?.body
    try {
      const errorObj = error as { context?: { body?: unknown } };
      const body = errorObj?.context?.body;

      if (body && typeof body === 'object') {
        const errorBody = body as { code?: string };
        const code = errorBody?.code;

        if (this.isValidErrorCode(code)) {
          return { ok: false, error: code };
        }
      }

      // Check if body is a string that needs parsing
      if (typeof body === 'string') {
        const parsed = JSON.parse(body) as { code?: string };
        if (this.isValidErrorCode(parsed?.code)) {
          return { ok: false, error: parsed.code };
        }
      }
    } catch {
      // JSON parse failed or other error - fall through to default
    }

    // Default to TRANSIENT for network/unknown errors
    return { ok: false, error: 'TRANSIENT' };
  }

  /**
   * Extract error code from data object (for 2xx error responses)
   */
  private extractErrorCode(data: unknown): FetchErrorCode {
    try {
      const dataObj = data as { code?: string };
      if (this.isValidErrorCode(dataObj?.code)) {
        return dataObj.code;
      }
    } catch {
      // Fall through to default
    }
    return 'UNKNOWN_ERROR';
  }

  /**
   * Type guard for valid FetchErrorCode values
   */
  private isValidErrorCode(code: unknown): code is FetchErrorCode {
    return (
      code === 'UNAUTHORIZED' ||
      code === 'BAD_REQUEST' ||
      code === 'PROVIDER_RATE_LIMITED' ||
      code === 'PROVIDER_AUTH_ERROR' ||
      code === 'TRANSIENT' ||
      code === 'UNKNOWN_ERROR'
    );
  }
}

// ============================================================================
// Service Class
// ============================================================================

/**
 * Economic Calendar Service
 *
 * Fetches economic calendar events from external providers, transforms them
 * to domain types, and handles deduplication against existing drafts.
 *
 * Usage:
 * ```ts
 * import { economicCalendarService } from '@/services/EconomicCalendarService';
 *
 * const result = await economicCalendarService.fetchEvents('2024-01-01', '2024-01-07', existingDrafts);
 * if (result.ok) {
 *   // result.events contains new (non-duplicate) EconomicEventDraft[]
 * } else {
 *   // result.error contains FetchErrorCode
 *   if (isTransientError(result.error)) {
 *     // Retry later
 *   }
 * }
 * ```
 */
export class EconomicCalendarService {
  private provider: EconomicCalendarProvider;

  constructor() {
    this.provider = new FinnhubProvider();
  }

  /**
   * Fetch economic events for a date range
   *
   * @param from - Start date (YYYY-MM-DD)
   * @param to - End date (YYYY-MM-DD)
   * @param existingDrafts - Existing drafts for deduplication
   * @returns Result object with new events or error code
   */
  async fetchEvents(
    from: string,
    to: string,
    existingDrafts: EconomicEventDraft[]
  ): Promise<FetchEventsResult> {
    // Fetch from provider
    const providerResult = await this.provider.fetchEvents(from, to);

    if (!providerResult.ok) {
      return { ok: false, error: providerResult.error };
    }

    // Build Set of existing providerEventIds for O(1) lookup
    // Only include non-empty providerEventId values
    const existingIds = new Set<string>(
      existingDrafts
        .map((draft) => draft.providerEventId)
        .filter((id): id is string => !!id)
    );

    // Transform and deduplicate events
    const newEvents: EconomicEventDraft[] = [];

    for (const finnhubEvent of providerResult.events) {
      // Transform to draft (returns null if missing required fields)
      const draft = transformToEventDraft(finnhubEvent);

      if (draft === null) {
        continue;
      }

      // Skip if duplicate (already exists or already seen in this batch)
      if (draft.providerEventId && existingIds.has(draft.providerEventId)) {
        continue;
      }

      // Add to results and mark as seen (intra-batch dedup)
      newEvents.push(draft);
      if (draft.providerEventId) {
        existingIds.add(draft.providerEventId);
      }
    }

    // Filter out skeleton events that have matching real events
    const filteredEvents = filterSkeletonEvents(newEvents);

    return { ok: true, events: filteredEvents };
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

/**
 * Singleton instance of EconomicCalendarService
 *
 * Use this shared instance for all economic calendar operations.
 */
export const economicCalendarService = new EconomicCalendarService();
